"use client";

import { Button } from "@/components/ui/Button";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useKeyPressed } from "@/hooks/useKeyPressed";
import { useOnClickOutside } from "@/hooks/useOnClickOutside";
import { dateFormats } from "@/utils/dateFormatter";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import "react-day-picker/style.css";
import { createPortal } from "react-dom";
import TripDatePickerContent from "./TripDatePickerContent";
import ViewportHeightWrapper from "@/components/ViewportHeightWrapper";

const GenericDateRangePicker = forwardRef(
  (
    {
      id,
      label,
      value,
      onSelect,
      placeholder,
      disabled,
      onPortalStateChange,
      onOpen,
      onDropdownOpen,
      isFromDate,
      mobileDepartureClick,
      mobileReturnClick,
      departureDate,
      returnDate,
      isRoundTrip,
      defaultMonth,
      publicHolidays,
      translationNamespace = "common",
      viewportHeightWrapperActive = false,
    },
    ref,
  ) => {
    const isMobile = useIsMobile();
    const t = useTranslationDetector(translationNamespace);

    const ANIMATION_STATES = useMemo(
      () => ({
        CLOSED: "closed",
        OPENING: "opening",
        OPEN: "open",
        CLOSING: "closing",
      }),
      [],
    );

    const [uiState, setUiState] = useState({
      animationState: ANIMATION_STATES.CLOSED,
      isFocused: false,
      activeTab: "calendar",
    });

    const isOpen = uiState.animationState !== ANIMATION_STATES.CLOSED;
    const isFocused = uiState.isFocused;
    const activeTab = uiState.activeTab;
    const shouldRenderPortal =
      isMobile &&
      (uiState.animationState === ANIMATION_STATES.OPENING ||
        uiState.animationState === ANIMATION_STATES.OPEN ||
        uiState.animationState === ANIMATION_STATES.CLOSING);
    const isAnimating = isMobile && uiState.animationState === ANIMATION_STATES.OPEN;
    const shouldRenderWebDropdown =
      !isMobile &&
      (uiState.animationState === ANIMATION_STATES.OPENING ||
        uiState.animationState === ANIMATION_STATES.OPEN ||
        uiState.animationState === ANIMATION_STATES.CLOSING);
    const isWebDropdownAnimating =
      !isMobile && uiState.animationState === ANIMATION_STATES.OPEN;
    const escapePressed = useKeyPressed("Escape");

    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const blurTimeoutRef = useRef(null);

    useEffect(() => {
      if (!isOpen && !returnDate) {
        onSelect({ from: departureDate, to: departureDate });
      }
    }, [isOpen, returnDate, onSelect, departureDate]);

    const handleClickOutside = useCallback(() => {
      if (isMobile && shouldRenderPortal) return;
      if (!isMobile && isOpen) {
        setUiState((prev) => ({
          ...prev,
          animationState: ANIMATION_STATES.CLOSING,
          isFocused: false,
        }));
      }
    }, [isMobile, isOpen, shouldRenderPortal, ANIMATION_STATES]);

    useEffect(() => {
      if (escapePressed && isOpen) {
        setUiState((prev) => ({
          ...prev,
          animationState: ANIMATION_STATES.CLOSING,
          isFocused: false,
        }));
      }
    }, [escapePressed, isOpen, ANIMATION_STATES]);

    useOnClickOutside(containerRef, handleClickOutside);

    useEffect(() => {
      if (onPortalStateChange && isMobile) {
        onPortalStateChange(shouldRenderPortal);
      }
    }, [shouldRenderPortal, isMobile, onPortalStateChange]);

    // Fire onOpen once when first opened (mobile portal or web dropdown)
    const hasFiredOpenRef = useRef(false);
    useEffect(() => {
      const opened = (isMobile && shouldRenderPortal) || (!isMobile && shouldRenderWebDropdown);
      if (opened && !hasFiredOpenRef.current) {
        hasFiredOpenRef.current = true;
        onOpen?.();
      }
    }, [isMobile, shouldRenderPortal, shouldRenderWebDropdown, onOpen]);

    // Fire onDropdownOpen every time dropdown opens (no control check)
    useEffect(() => {
      const opened = (isMobile && shouldRenderPortal) || (!isMobile && shouldRenderWebDropdown);
      if (opened) {
        onDropdownOpen?.();
      }
    }, [isMobile, shouldRenderPortal, shouldRenderWebDropdown]);

    useEffect(() => {
      if (uiState.animationState === ANIMATION_STATES.OPENING) {
        const timer = setTimeout(() => {
          setUiState((prev) => ({ ...prev, animationState: ANIMATION_STATES.OPEN }));
        }, 10);
        return () => clearTimeout(timer);
      } else if (uiState.animationState === ANIMATION_STATES.CLOSING) {
        const timer = setTimeout(() => {
          setUiState((prev) => ({ ...prev, animationState: ANIMATION_STATES.CLOSED }));
        }, isMobile ? 300 : 200);
        return () => clearTimeout(timer);
      }
    }, [uiState.animationState, isMobile, ANIMATION_STATES]);

    const handleDateSelect = useCallback(
      (date, triggerDate) => {
        if (blurTimeoutRef.current) {
          clearTimeout(blurTimeoutRef.current);
          blurTimeoutRef.current = null;
        }
        if (!returnDate && !isMobile) {
          setUiState((prev) => ({
            ...prev,
            animationState: ANIMATION_STATES.CLOSING,
            isFocused: false,
          }));
        }
        onSelect?.(date, triggerDate);
      },
      [onSelect, returnDate, ANIMATION_STATES.CLOSING, isMobile],
    );

    const handlePortalClose = useCallback(() => {
      setUiState((prev) => ({
        ...prev,
        animationState: ANIMATION_STATES.CLOSING,
        isFocused: false,
      }));
    }, [ANIMATION_STATES]);

    const handleFocus = useCallback(() => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }
      setUiState((prev) => ({
        ...prev,
        animationState: ANIMATION_STATES.OPENING,
        isFocused: true,
      }));
    }, [ANIMATION_STATES]);

    useImperativeHandle(
      ref,
      () => ({
        open: () => {
          setUiState((prev) => ({
            ...prev,
            animationState: ANIMATION_STATES.OPENING,
            isFocused: true,
          }));
        },
        close: () => {
          setUiState((prev) => ({
            ...prev,
            animationState: ANIMATION_STATES.CLOSING,
            isFocused: false,
          }));
        },
        isOpen: () => uiState.animationState !== ANIMATION_STATES.CLOSED,
      }),
      [ANIMATION_STATES.OPENING, ANIMATION_STATES.CLOSING, uiState.animationState],
    );

    const handleBlur = useCallback(() => {
      if (isMobile && shouldRenderPortal) return;
      if (!isMobile) return;
      blurTimeoutRef.current = setTimeout(() => {
        setUiState((prev) => ({
          ...prev,
          animationState: ANIMATION_STATES.CLOSING,
          isFocused: false,
        }));
      }, 150);
    }, [isMobile, shouldRenderPortal, ANIMATION_STATES.CLOSING]);

    const containerClasses = useMemo(
      () =>
        clsx(
          "border-primary-weak relative flex h-[68px] w-full items-center border-b duration-300 ease-in-out md:h-[66px] md:rounded-lg md:border-0",
          isFocused && "md:ring-primary md:ring-2",
        ),
      [isFocused],
    );

    const labelClasses = useMemo(
      () =>
        clsx(
          "text-foreground-muted pointer-events-none absolute left-[18px] transition-all duration-300 ease-in-out md:left-5",
          isFocused || value ? "top-[11.5px] text-xs" : "top-1/2 -translate-y-1/2 text-base",
        ),
      [isFocused, value],
    );

    const inputClasses = useMemo(
      () =>
        clsx(
          "selection:bg-secondary-weak h-full w-full rounded-lg border-0 px-[18px] font-medium transition-all duration-300 ease-in-out focus:ring-0 focus:outline-none md:px-5",
          isFocused || value
            ? "pt-[26px] pb-[12px] md:pt-[31.5px] md:pb-[13.5px]"
            : "py-[12px] md:py-[22.5px]",
        ),
      [isFocused, value],
    );

    const portalContainerClasses = useMemo(() => {
      return clsx(
        "fixed inset-0 z-50 flex h-full flex-col bg-white",
        "transform transition-all duration-300",
        isAnimating ? "translate-y-0 ease-out" : "translate-y-full ease-in",
      );
    }, [isAnimating]);

    const webDropdownClasses = useMemo(() => {
      return clsx(
        "shadow-xxl absolute top-full -left-[100px] z-50 mt-2 w-[860px] min-w-[320px] overflow-hidden rounded-lg bg-white",
        "origin-top transform transition-all duration-200 ease-out",
        isWebDropdownAnimating ? "translate-y-0 scale-100 opacity-100" : "-translate-y-2 scale-95 opacity-0",
      );
    }, [isWebDropdownAnimating]);

    const safePublicHolidays = useMemo(
      () => (Array.isArray(publicHolidays) ? publicHolidays : []),
      [publicHolidays],
    );

    return (
      <>
        <div className="relative w-full" ref={containerRef} data-datepicker="true">
          <div className={containerClasses}>
            <div className="relative w-1/2 h-full">
              {label && (
                <label htmlFor={id} className={labelClasses}>
                  {t("departureDate")}
                </label>
              )}
              <input
                ref={inputRef}
                type="text"
                id={id}
                readOnly
                value={dateFormats.dayLongMonthShortDay(value?.from)}
                placeholder={placeholder}
                onFocus={handleFocus}
                onBlur={handleBlur}
                className={inputClasses}
              />
            </div>
            <span className="bg-border h-[1px] w-4"></span>
            <div className="relative w-1/2 h-full">
              {label && (
                <label htmlFor={id} className={labelClasses}>
                  {t("returnDate")}
                </label>
              )}
              <input
                ref={inputRef}
                type="text"
                id={id}
                readOnly
                value={returnDate ? dateFormats.dayLongMonthShortDay(value?.to) : ""}
                placeholder={placeholder}
                onFocus={handleFocus}
                onBlur={handleBlur}
                className={inputClasses}
              />
            </div>
          </div>

          {!isMobile && shouldRenderWebDropdown && (
            <div className={webDropdownClasses}>
              <ViewportHeightWrapper defaultMaxHeight={525} bottomPadding={16} enabled={viewportHeightWrapperActive}>
                <TripDatePickerContent
                  mode="range"
                  activeTab={activeTab}
                  setActiveTab={(tab) => setUiState((prev) => ({ ...prev, activeTab: tab }))}
                  value={value}
                  handleDateSelect={handleDateSelect}
                  defaultMonth={defaultMonth}
                  getPriceByDate={() => null}
                  isRoundTrip={isRoundTrip}
                  mobileDepartureClick={mobileDepartureClick}
                  isFromDate={isFromDate}
                  departureDate={departureDate}
                  mobileReturnClick={mobileReturnClick}
                  returnDate={returnDate}
                  isMultiCity={false}
                  publicHolidays={safePublicHolidays}
                  translationNamespace={translationNamespace}
                />
              </ViewportHeightWrapper>
            </div>
          )}
        </div>

        {shouldRenderPortal &&
          isMobile &&
          createPortal(
            <div className={portalContainerClasses}>
              <div className="flex shrink-0 items-center justify-between px-4 py-4">
                <span className="w-full text-center text-lg font-semibold">{label}</span>
                <button onClick={handlePortalClose} className="self-end">
                  <FontAwesomeIcon icon={byPrefixAndName.far["xmark"]} className="text-2xl" />
                </button>
              </div>

              <div className="min-h-0 flex-1">
                <TripDatePickerContent
                  mode="range"
                  activeTab={activeTab}
                  setActiveTab={(tab) => setUiState((prev) => ({ ...prev, activeTab: tab }))}
                  value={value}
                  handleDateSelect={handleDateSelect}
                  defaultMonth={defaultMonth}
                  getPriceByDate={() => null}
                  isRoundTrip={isRoundTrip}
                  mobileDepartureClick={mobileDepartureClick}
                  isFromDate={isFromDate}
                  departureDate={departureDate}
                  mobileReturnClick={mobileReturnClick}
                  returnDate={returnDate}
                  isMultiCity={false}
                  publicHolidays={safePublicHolidays}
                  translationNamespace={translationNamespace}
                />
                <div className="border-primary-weak relative z-[1] shrink-0 border-t bg-white p-4 md:hidden">
                  <Button className="w-full" size="lg" disabled={!returnDate} onClick={handlePortalClose}>
                    {t("applyButton")}
                  </Button>
                </div>
              </div>
            </div>,
            document.body,
          )}
      </>
    );
  },
);

GenericDateRangePicker.displayName = "GenericDateRangePicker";

export default GenericDateRangePicker;


